import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Star, TrendingDown, ArrowRight, BarChart3 } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20
    }
  }
};
const decorationVariants = {
  hidden: {
    opacity: 0,
    scale: 0.8
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 1,
      ease: 'easeOut'
    }
  }
};
export function InfographicPost() {
  return <motion.div initial="hidden" animate="visible" variants={containerVariants} className="relative w-full max-w-[600px] aspect-square bg-wp-navy rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl shadow-wp-teal/10 flex flex-col p-6 sm:p-10 font-inter border border-white/5" data-id="element-3">
      {/* Background Decorative Elements */}
      <motion.div variants={decorationVariants} className="absolute top-0 right-0 -mt-10 -mr-10 opacity-20 pointer-events-none" data-id="element-4">
        <svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" data-id="element-5">
          <defs data-id="element-6">
            <pattern id="dotGrid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse" data-id="element-7">
              <circle cx="2" cy="2" r="2" fill="#4ECDC4" data-id="element-8" />
            </pattern>
          </defs>
          <rect width="200" height="200" fill="url(#dotGrid)" data-id="element-9" />
        </svg>
      </motion.div>

      <motion.div variants={decorationVariants} className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full border-[1px] border-wp-coral/10 pointer-events-none" data-id="element-10" />
      <motion.div variants={decorationVariants} className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full border-[1px] border-wp-teal/10 pointer-events-none" data-id="element-11" />

      <motion.div variants={decorationVariants} className="absolute top-1/3 right-10 opacity-10 pointer-events-none" data-id="element-12">
        <svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg" data-id="element-13">
          <path d="M0 60 L60 0" stroke="#FF6B35" strokeWidth="2" strokeDasharray="6 6" data-id="element-14" />
          <path d="M0 40 L40 0" stroke="#FF6B35" strokeWidth="2" strokeDasharray="6 6" data-id="element-15" />
        </svg>
      </motion.div>

      {/* Header */}
      <motion.div variants={itemVariants} className="relative z-10 flex justify-between items-center mb-6 sm:mb-8" data-id="element-16">
        <div className="flex items-center gap-2" data-id="element-17">
          <div className="w-6 h-6 bg-wp-teal rounded-sm flex items-center justify-center" data-id="element-18">
            <div className="w-3 h-3 bg-wp-navy rounded-full" data-id="element-19" />
          </div>
          <span className="font-bold text-white tracking-tight text-lg" data-id="element-20">
            WeddingPitch
          </span>
        </div>
        <div className="flex items-center gap-3" data-id="element-21">
          <div className="h-[1px] w-8 bg-wp-teal/50" data-id="element-22" />
          <span className="text-wp-teal font-bold text-xs tracking-[0.2em] uppercase" data-id="element-23">
            The Data Is Clear
          </span>
        </div>
      </motion.div>

      {/* Main Stats Content */}
      <div className="flex-1 flex flex-col justify-between relative z-10 space-y-4 sm:space-y-0" data-id="element-24">
        {/* Stat 1 */}
        <motion.div variants={itemVariants} className="flex items-center gap-4 sm:gap-8" data-id="element-25">
          <div className="relative flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center" data-id="element-26">
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100" data-id="element-27">
              <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" data-id="element-28" />
              <circle cx="50" cy="50" r="44" fill="none" stroke="#4ECDC4" strokeWidth="8" strokeDasharray="276.46" strokeDashoffset="19.35" strokeLinecap="round" className="opacity-80" data-id="element-29" />
            </svg>
            <div className="flex items-baseline" data-id="element-30">
              <span className="text-5xl sm:text-7xl font-black text-wp-coral tracking-tighter" data-id="element-31">
                93
              </span>
              <span className="text-2xl sm:text-4xl font-bold text-wp-coral" data-id="element-32">
                %
              </span>
            </div>
          </div>
          <p className="text-white/90 font-bold text-sm sm:text-lg leading-snug uppercase tracking-wide max-w-[240px]" data-id="element-33">
            of couples read reviews before booking
          </p>
        </motion.div>

        {/* Divider */}
        <motion.div variants={itemVariants} className="w-full h-[1px] bg-gradient-to-r from-white/10 via-white/5 to-transparent" data-id="element-34" />

        {/* Stat 2 */}
        <motion.div variants={itemVariants} className="flex flex-row-reverse items-center justify-end gap-4 sm:gap-8 text-right" data-id="element-35">
          <div className="relative flex-shrink-0" data-id="element-36">
            <div className="flex items-baseline justify-end" data-id="element-37">
              <span className="text-5xl sm:text-7xl font-black text-wp-teal tracking-tighter" data-id="element-38">
                72
              </span>
              <span className="text-2xl sm:text-4xl font-bold text-wp-teal" data-id="element-39">
                %
              </span>
            </div>
            <div className="h-2 w-full bg-white/10 rounded-full mt-2 overflow-hidden" data-id="element-40">
              <div className="h-full bg-wp-teal w-[72%] rounded-full" data-id="element-41" />
            </div>
          </div>
          <div className="flex flex-col items-end gap-2" data-id="element-42">
            <div className="flex gap-1 text-wp-coral" data-id="element-43">
              <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-current" data-id="element-44" />
              <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-current" data-id="element-45" />
              <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-current" data-id="element-46" />
              <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-current" data-id="element-47" />
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-white/20" data-id="element-48" />
            </div>
            <p className="text-white/90 font-bold text-sm sm:text-lg leading-snug uppercase tracking-wide max-w-[240px]" data-id="element-49">
              won't book a vendor with less than 4 stars
            </p>
          </div>
        </motion.div>

        {/* Divider */}
        <motion.div variants={itemVariants} className="w-full h-[1px] bg-gradient-to-l from-white/10 via-white/5 to-transparent" data-id="element-50" />

        {/* Stat 3 */}
        <motion.div variants={itemVariants} className="relative bg-wp-charcoal border border-white/5 p-4 sm:p-5 rounded-xl overflow-hidden group" data-id="element-51">
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-wp-coral/10 to-transparent pointer-events-none" data-id="element-52" />
          <TrendingDown className="absolute right-4 top-1/2 -translate-y-1/2 w-16 h-16 text-wp-coral/20 group-hover:text-wp-coral/40 transition-colors duration-500" data-id="element-53" />

          <div className="relative z-10 flex flex-col gap-1 sm:gap-2" data-id="element-54">
            <div className="flex items-center gap-3" data-id="element-55">
              <span className="text-4xl sm:text-5xl font-black text-wp-coral" data-id="element-56">
                1
              </span>
              <span className="text-white/60 font-bold text-sm sm:text-base uppercase tracking-widest" data-id="element-57">
                bad review
              </span>
            </div>
            <div className="flex items-center gap-3 pl-2 sm:pl-4" data-id="element-58">
              <span className="text-2xl sm:text-3xl font-black text-wp-teal" data-id="element-59">
                =
              </span>
              <span className="text-5xl sm:text-6xl font-black text-wp-coral" data-id="element-60">
                30
              </span>
              <span className="text-white/90 font-bold text-sm sm:text-base uppercase tracking-widest leading-tight max-w-[120px]" data-id="element-61">
                lost leads
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Footer / CTA */}
      <motion.div variants={itemVariants} className="mt-6 sm:mt-8 relative z-10" data-id="element-62">
        <p className="text-center text-white/90 font-semibold text-sm sm:text-base mb-3 sm:mb-4" data-id="element-63">
          Don't leave your reputation to chance.
        </p>
        <button className="w-full bg-wp-coral hover:bg-[#ff7e4f] text-white font-bold py-3 sm:py-4 px-6 rounded-lg flex justify-between items-center uppercase tracking-wide text-xs sm:text-sm transition-colors duration-300 group shadow-lg shadow-wp-coral/20" data-id="element-64">
          <span data-id="element-65">Manage Your Reviews</span>
          <div className="flex items-center gap-2" data-id="element-66">
            <span className="lowercase normal-case font-semibold opacity-90 hidden sm:inline-block" data-id="element-67">
              weddingpitch.com
            </span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-68" />
          </div>
        </button>
      </motion.div>
    </motion.div>;
}